import { 
  doc, 
  updateDoc, 
  DocumentReference, 
  writeBatch, 
  getDoc, 
  deleteDoc, 
  collection, 
  query, 
  where, 
  getDocs 
} from 'firebase/firestore';
import { db } from '../firebase/firebase';
import { IAuthor } from '../types/Author';
import { skipInDemoMode } from './base/demoModeUtils';
import { BaseCRUDManager } from './base/BaseCRUDManager';

export const AuthorManager = {
  getAllAuthors: async (): Promise<IAuthor[]> => {
    try {
      return (await BaseCRUDManager.getAll<any>('Authors', {
        idField: 'authorKey'
      })) as IAuthor[];
    } catch (error) {
      return [];
    }
  },

  getAuthor: async (authorKey: string): Promise<IAuthor | null> => {
    try {
      return (await BaseCRUDManager.getById<any>('Authors', authorKey, {
        idField: 'authorKey'
      })) as IAuthor | null;
    } catch (error) {
      return null;
    }
  },

  updateAuthor: async (authorKey: string, updatedData: Partial<IAuthor>): Promise<void> => {
    // In demo mode, skip Firebase
    await skipInDemoMode(async () => {
      try {
      const authorDocRef: DocumentReference = doc(db, 'Authors', authorKey);
      

      // Check if the document exists before updating
      const docSnapshot = await getDoc(authorDocRef);
      if (!docSnapshot.exists()) {
        throw new Error(`Author document with key ${authorKey} does not exist`);
      }

      // Remove any undefined or null values from updatedData
      const cleanedData: Partial<IAuthor> = {};
      for (const [key, value] of Object.entries(updatedData)) {
        if (value !== undefined && value !== null) {
          if (key === 'tags' && typeof value === 'object') {
            cleanedData.tags = value as { authorTags: string[] };
          } else {
            (cleanedData as any)[key] = value;
          }
        }
      }

      // Perform the update
      await updateDoc(authorDocRef, cleanedData);
      
    } catch (error) {
      console.error("AuthorManager: Error updating author:", error);
      if (error instanceof Error) {
        console.error("AuthorManager: Error message:", error.message);
        console.error("AuthorManager: Error stack:", error.stack);
      }
      if (error instanceof Error && 'code' in error) {
        console.error("AuthorManager: Error code:", (error as any).code);
      }
      throw error;
      }
    });
  },
  getAuthorByName: async (name: string): Promise<IAuthor | null> => {
    try {
      const authors = await BaseCRUDManager.query<any>('Authors', {
        filters: [
          { field: 'name', operator: '==', value: name }
        ]
      }, {
        idField: 'authorKey'
      });
      
      return authors.length > 0 ? (authors[0] as IAuthor) : null;
    } catch (error) {
      return null;
    }
  },
// createAuthor: async (authorKey: string, newAuthorData: Partial<IAuthor>): Promise<IAuthor> => { - prevous version for reference
  createAuthor: async (authorKey: string, authorData: Omit<IAuthor, 'authorKey'>): Promise<string> => {
    // In demo mode, skip Firebase and just return the authorKey
    const result = await skipInDemoMode(async () => {
      try {
      // Remove undefined values before saving to Firebase
      const cleanedData: any = {
        isClaimed: authorData.isClaimed ?? false
      };
      
      // Only include defined values
      if (authorData.name !== undefined) cleanedData.name = authorData.name;
      if (authorData.bio !== undefined) cleanedData.bio = authorData.bio;
      if (authorData.website !== undefined) cleanedData.website = authorData.website;
      if (authorData.image !== undefined) cleanedData.image = authorData.image;
      if (authorData.tags !== undefined) cleanedData.tags = authorData.tags;
      if (authorData.userKey !== undefined) cleanedData.userKey = authorData.userKey;
      if (authorData.sourceView !== undefined) cleanedData.sourceView = authorData.sourceView;
      
      await BaseCRUDManager.create('Authors', cleanedData, {
        idField: 'authorKey',
        customId: authorKey,
        autoTimestamps: false // Authors may have their own timestamp handling
      });
      
        return authorKey; // Return the authorKey that was passed in
      } catch (error) {
        console.error("Error creating new author:", error);
        throw error;
      }
    });
    return result || authorKey;
  },

  getAuthorsByName: async (name: string): Promise<IAuthor[]> => {
    try {
      const authors = await BaseCRUDManager.query<any>('Authors', {
        filters: [
          { field: 'name', operator: '==', value: name }
        ]
      }, {
        idField: 'authorKey'
      });
      
      // Filter the results to include only unclaimed authors
      return authors.filter(author => {
        return !('isClaimed' in author) || author.isClaimed === false || author.isClaimed === null;
      }) as IAuthor[];
    } catch (error) {
      console.error("Error fetching authors by name:", error);
      return [];
    }
  },

  mergeAuthors: async (
    authorKeys: string[], 
    userKey: string, 
    finalAuthorKey: string,
    preferences?: {
      bio?: string;
      website?: string;
      image?: string;
    }
  ): Promise<IAuthor> => {
    const batch = writeBatch(db);
    const authors = await Promise.all(authorKeys.map(key => getDoc(doc(db, 'Authors', key))));
    
    // Find the base author (the one we're merging into)
    const baseAuthor = authors.find(doc => doc.id === finalAuthorKey)?.data() as IAuthor;
    
    const mergedAuthor: IAuthor = {
      authorKey: finalAuthorKey,
      name: baseAuthor?.name || authors[0].data()?.name || '',
      userKey,
      tags: { authorTags: [] },
      isClaimed: true,
      // Use preferences if provided, otherwise keep base author's values
      bio: preferences?.bio || baseAuthor?.bio || null,
      website: preferences?.website || baseAuthor?.website || null,
      image: preferences?.image || baseAuthor?.image || null,
    };

    // Merge tags from all authors
    authors.forEach(author => {
      const data = author.data() as IAuthor;
      mergedAuthor.tags.authorTags = Array.from(new Set([...mergedAuthor.tags.authorTags, ...(data.tags?.authorTags || [])]));
    });

    // Update or create the merged author document
    batch.set(doc(db, 'Authors', finalAuthorKey), mergedAuthor);

    // Delete other author documents
    authorKeys.forEach(key => {
      if (key !== finalAuthorKey) {
        batch.delete(doc(db, 'Authors', key));
      }
    });

    await batch.commit();
    return mergedAuthor;
  },

  claimAuthor: async (authorKey: string, userKey: string): Promise<void> => {
    try {
      const authorRef = doc(db, 'Authors', authorKey);
      await updateDoc(authorRef, { userKey, isClaimed: true });
      
    } catch (error) {
      console.error("Error claiming author:", error);
      throw error;
    }
  },

  deleteAuthor: async (authorKey: string): Promise<void> => {
    try {
      await deleteDoc(doc(db, 'Authors', authorKey));
      
    } catch (error) {
      console.error("Error deleting author:", error);
      throw error;
    }
  },

  getAuthorByUserKey: async (userKey: string): Promise<IAuthor | null> => {
    try {
      const authorsRef = collection(db, 'Authors');
      const q = query(authorsRef, where('userKey', '==', userKey));
      const querySnapshot = await getDocs(q);
      
      if (querySnapshot.empty) {
        return null;
      }

      const authorDoc = querySnapshot.docs[0];
      return { authorKey: authorDoc.id, ...authorDoc.data() } as IAuthor;
    } catch (error) {
      console.error("Error fetching author by userKey:", error);
      return null;
    }
  },

  getAuthorByKey: async (authorKey: string): Promise<IAuthor | null> => {
    try {
      const authorDocRef = doc(db, 'Authors', authorKey);
      const docSnapshot = await getDoc(authorDocRef);
      if (docSnapshot.exists()) {
        return { authorKey: docSnapshot.id, ...docSnapshot.data() } as IAuthor;
      } else {
        
        return null;
      }
    } catch (error) {
      console.error("Error fetching author by key:", error);
      return null;
    }
  },
};

export default AuthorManager;